import React, { forwardRef, useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import {
  formatRelativeTime,
  getFullDate,
} from "../../utils/formatUtils";
import {
  highlightHtmlContent,
} from "../../utils/htmlUtils";
import {
  getNoteInitials,
} from "../../utils/noteUtils";
import PriorityIndicator from "./PriorityIndicator";
import UserStatusIndicator from "../Tooltips/UserStatusIndicator";

// Create a root portal component
const RootPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? ReactDOM.createPortal(children, document.body) : null;
};

// Task Status Mapping
const TASK_STATUS_MAP = {
  1: { id: 1, name: "To Do", icon: "fa-circle", color: "#6c757d", bgColor: "#e9ecef" },
  2: { id: 2, name: "In Progress", icon: "fa-spinner", color: "#fd7e14", bgColor: "#fff3e0" },
  3: { id: 3, name: "Archived", icon: "fa-archive", color: "#6c757d", bgColor: "#e9ecef" },
  4: { id: 4, name: "Blocked", icon: "fa-times", color: "#dc3545", bgColor: "#f8d7da" },
  5: { id: 5, name: "Done", icon: "fa-check-circle", color: "#28a745", bgColor: "#d4edda" },
};

const NoteCard = forwardRef((props, ref) => {
  const {
    note,
    displayNotes, 
    isOriginalNoteExists,
    selectedRow,
    searchTerm,
    viewMode,
    handleRowClick,
    handleRowDoubleClick,
    handleAddFromRow,
    handleEdit,
    handleDelete,
    handleViewAttachments,
    handleReplyToNote,
    handlePriorityClick,
    handlePriorityMouseEnter,
    handlePriorityMouseLeave,
    handleNoteTextMouseEnter,
    handleNoteTextMouseLeave,
    handleLinkedNoteClick,
    handleLinkedNoteMouseEnter,
    handleLinkedNoteMouseLeave,
    renderCardImageIcon,
    isNoteReply,
    getReplyNoteId,
    userStatusMap,
    loadingUsers,
    getPriorityValue,
    manuallyUpdatedPriorities,
    shouldShowNotePopup,
    handleProjectClick,
    handleJobClick,
    handleTaskClick,
    handleUserNameClick,
    isProjectFiltered,
    isJobFiltered,
    isTaskFiltered,
    isUserNameFiltered,
    // Task-specific props
    taskData = {},
    onTaskDueDateSelect,
    onTaskAssigneeSelect,
    onTaskStatusSelect,
    taskUsers = [],
    taskStatuses = [],
    loadingTaskUsers = false,
    userId,
    refreshNotes,
    job,
    onFilterByProject,
    onFilterByJob,
  } = props;

  const priorityValue = getPriorityValue(note, manuallyUpdatedPriorities);
/*   const isReply = isNoteReply(note.id);
  const originalNoteId = isReply ? getReplyNoteId(note.id) : null;
  const originalNoteExists = isReply && originalNoteId 
    ? isOriginalNoteExists(originalNoteId)
    : false;

  const shouldShowLink = isReply && originalNoteId && originalNoteExists; */

  const isReply = note.isReply === 1; // or note.isReply === true if it's boolean
const originalNoteId = isReply ? getReplyNoteId(note.id): null;
const originalNoteExists = isReply && originalNoteId && originalNoteId !== 0
  ? isOriginalNoteExists(originalNoteId)
  : false;
const shouldShowLink = isReply && originalNoteExists && originalNoteId !== 0;
  const [showJobTooltip, setShowJobTooltip] = useState(false);
  const [showProjectTooltip, setShowProjectTooltip] = useState(false);

  // Task popup states
  const [showTaskDueDatePopup, setShowTaskDueDatePopup] = useState(false);
  const [showTaskAssigneePopup, setShowTaskAssigneePopup] = useState(false);
  const [showTaskStatusPopup, setShowTaskStatusPopup] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Calendar mode state
  const [calendarMode, setCalendarMode] = useState('dueDate'); // 'dueDate' or 'duration'

  // Initialize task data from note
  const [selectedDueDate, setSelectedDueDate] = useState(note.dueDate || null);
  const [selectedStartDate, setSelectedStartDate] = useState(note.startDate || null);
  const [selectedEndDate, setSelectedEndDate] = useState(note.endDate || null);
  
  // Multiple assignees state
  const [selectedAssignees, setSelectedAssignees] = useState(() => {
    // Check if there are assigned users
    if (note.assignedUsers && note.assignedUsers.length > 0) {
      return note.assignedUsers.map(user => ({
        id: user.userId,
        name: user.fullName || user.username
      }));
    }
    // Fallback to old properties if they exist
    return note.assigneeId ? [{ id: note.assigneeId, name: note.assigneeName }] : [];
  });
  
  const [selectedStatus, setSelectedStatus] = useState(
    TASK_STATUS_MAP[note.status] || TASK_STATUS_MAP[1]
  );
  
  // Temporary selection states for duration mode
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);

  // Users state
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsersList, setLoadingUsersList] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // Loading state for updates
  const [updatingTask, setUpdatingTask] = useState(false);
  const [updatingAssignees, setUpdatingAssignees] = useState(false);

  console.log(note)

  // Popup position state
  const [popupPosition, setPopupPosition] = useState({
    dueDate: { top: 0, left: 0 },
    assignee: { top: 0, left: 0 },
    status: { top: 0, left: 0 }
  });

  // Refs for the icons
  const calendarIconRef = useRef(null);
  const assigneeIconRef = useRef(null);
  const statusIconRef = useRef(null);

  // API base URL
  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';

  // Determine if this is a task
  const isTask = note.itemType === "Task";

  // Function to update task with toast notifications
  const updateTask = useCallback(async (updates) => {
    if (!note.taskId) {
      console.error("No taskId found for note:", note);
      toast.error("Cannot update task: Task ID not found");
      return;
    }

    setUpdatingTask(true);

    // Determine what's being updated for the toast message
    let updateType = '';
    if (updates.dueDate !== undefined) updateType = 'Due date';
    else if (updates.startDate !== undefined || updates.endDate !== undefined) updateType = 'Duration';
    else if (updates.status !== undefined) updateType = 'Status';

    try {
      // Build the complete task payload
      const payload = {
        title: note.title || note.note || "",
        description: note.description || note.note || "",
        startDate: updates.startDate !== undefined ? updates.startDate : (note.startDate || new Date().toISOString()),
        endDate: updates.endDate !== undefined ? updates.endDate : (note.endDate || null),
        dueDate: updates.dueDate !== undefined ? updates.dueDate : (selectedDueDate || null),
        jobId: note.jobId || 0,
        status: selectedStatus?.id || 1,
        ...updates
      };

      console.log("Updating task with payload:", payload);

      const response = await fetch(`${apiUrl}/api/JobTasks/${note.taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update task: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log("Task updated successfully:", result);

      // Show success toast
      if (updateType) {
        toast.success(`${updateType} updated successfully`);
      } else {
        toast.success("Task updated successfully");
      }

      // Call the parent's callback if provided
      if (updates.dueDate !== undefined && onTaskDueDateSelect) {
        onTaskDueDateSelect(note, updates.dueDate);
      }
      if ((updates.startDate !== undefined || updates.endDate !== undefined) && onTaskDueDateSelect) {
        onTaskDueDateSelect(note, { startDate: updates.startDate, endDate: updates.endDate });
      }
      if (updates.status !== undefined && onTaskStatusSelect) {
        onTaskStatusSelect(note, { id: updates.status });
      }

      // Trigger refresh if available
      if (refreshNotes) {
        refreshNotes();
      }

      return result;
    } catch (error) {
      console.error("Error updating task:", error);

      let errorMessage = `Failed to update ${updateType.toLowerCase() || 'task'}`;

      if (error.message.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message.includes('500')) {
        errorMessage = "Server error. Please try again later.";
      } else {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setUpdatingTask(false);
    }
  }, [note, selectedDueDate, selectedStatus, userId, apiUrl, onTaskDueDateSelect, onTaskStatusSelect, refreshNotes]);

  // Function to add assignee to task
  const addAssignee = useCallback(async (user) => {
    console.log(user)
    if (!note.taskId || !userId) {
      toast.error("Cannot add assignee: Missing task ID or user ID");
      return;
    }

    setUpdatingAssignees(true);
    
    try {
      const payload = {
        userId: user.id,
        jobTaskId: note.taskId,
        assignedById: parseInt(userId)
      };

      console.log("Adding assignee with payload:", payload);

      const response = await fetch(`${apiUrl}/api/UserJobTaskAssignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        
        // Check if it's a duplicate assignment error
        if (response.status === 409 || errorText.includes('already assigned')) {
          toast.error(`${user.name} is already assigned to this task`);
          return;
        }
        
        throw new Error(`Failed to add assignee: ${response.status} - ${errorText}`);
      }

      // Add user to selected assignees list
      setSelectedAssignees(prev => [...prev, { id: user.id, name: user.name }]);
      
      toast.success(`${user.name} assigned successfully`);
      
      // Call parent callback if provided
      if (onTaskAssigneeSelect) {
        onTaskAssigneeSelect(note, { assignees: [...selectedAssignees, user] });
      }

      // Trigger refresh if available
      if (refreshNotes) {
        refreshNotes();
      }
      
    } catch (error) {
      console.error("Error adding assignee:", error);
      
      let errorMessage = `Failed to assign ${user.name}`;
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message.includes('500')) {
        errorMessage = "Server error. Please try again later.";
      } else {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUpdatingAssignees(false);
    }
  }, [note.taskId, userId, apiUrl, onTaskAssigneeSelect, refreshNotes, selectedAssignees]);

  // Function to remove assignee from task
  const removeAssignee = useCallback(async (user) => {
    if (!note.taskId) {
      toast.error("Cannot remove assignee: Missing task ID");
      return;
    }

    setUpdatingAssignees(true);
    
    try {
      // Using the DELETE endpoint: api/UserJobTaskAssignments/task/{taskId}/user/{userId}
      const response = await fetch(
        `${apiUrl}/api/UserJobTaskAssignments/task/${note.taskId}/user/${user.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok && response.status !== 404) {
        const errorText = await response.text();
        throw new Error(`Failed to remove assignee: ${response.status} - ${errorText}`);
      }

      // Remove user from selected assignees list
      setSelectedAssignees(prev => prev.filter(a => a.id !== user.id));
      
      toast.success(`${user.name} removed successfully`);
      
      // Call parent callback if provided
      if (onTaskAssigneeSelect) {
        onTaskAssigneeSelect(note, { assignees: selectedAssignees.filter(a => a.id !== user.id) });
      }

      // Trigger refresh if available
      if (refreshNotes) {
        refreshNotes();
      }
      
    } catch (error) {
      console.error("Error removing assignee:", error);
      
      let errorMessage = `Failed to remove ${user.name}`;
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message.includes('500')) {
        errorMessage = "Server error. Please try again later.";
      } else {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUpdatingAssignees(false);
    }
  }, [note.taskId, apiUrl, onTaskAssigneeSelect, refreshNotes]);

  // Function to fetch users
  const fetchUsers = useCallback(async () => {
    
    if (!userId) {
      console.log("No userId provided");
      setUsersError("User ID not available");
      setLoadingUsersList(false);
      return;
    }

    setLoadingUsersList(true);
    setUsersError(null);

    try {
      const url = `${apiUrl}/api/Permissions/permissions-by-job/${job?.jobId || note.jobId}`;
      console.log("Fetching users from:", url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      console.log("Users data received:", data);

if (data && data.permissions && Array.isArray(data.permissions)) {
      // Check for duplicate IDs and ensure uniqueness
      const uniqueUsersMap = new Map();
      
      data.permissions.forEach((user) => {
        if (!uniqueUsersMap.has(user.userId)) {
          uniqueUsersMap.set(user.userId, {
            id: user.userId,
            name: user.userName,
            color: getRandomColor(user.userId),
          });
        }
      });
      
      const transformedUsers = Array.from(uniqueUsersMap.values());
      console.log("Transformed users:", transformedUsers);
      setAvailableUsers(transformedUsers);
      } else {
        console.log("No usernames array in response");
        setAvailableUsers([]);
        toast.error("No users found");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsersError(error.message);
      setAvailableUsers([]);
      toast.error("Failed to load users list");
    } finally {
      setLoadingUsersList(false);
    }
  }, [userId, apiUrl, job, note.jobId]);

  // Fetch users when assignee popup is opened
  useEffect(() => {
    if (showTaskAssigneePopup) {
      console.log("Assignee popup opened, fetching users...");
      fetchUsers();
    }
  }, [showTaskAssigneePopup, fetchUsers]);

  // Function to calculate popup position
  const calculatePopupPosition = useCallback((elementRef, popupType = 'calendar') => {
    if (!elementRef.current) return { top: 0, left: 0 };

    const rect = elementRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Different popup heights based on type
    let popupHeight = 400; // default for calendar
    let popupWidth = 280;  // default width
    
    if (popupType === 'assignee') {
      popupHeight = 350;
      popupWidth = 240;
    } else if (popupType === 'status') {
      popupHeight = 240; // Status popup is smaller
      popupWidth = 200;
    }

    let top = rect.bottom + 5;
    let left = rect.left;

    // Check if popup would go below viewport
    if (top + popupHeight > viewportHeight) {
      top = rect.top - popupHeight + 80;
    }

    // Check if popup would go beyond right edge
    if (left + popupWidth > viewportWidth) {
      left = viewportWidth - popupWidth - 10;
    }

    // Ensure popup doesn't go above viewport
    if (top < 0) top = 10;
    // Ensure popup doesn't go left of viewport
    if (left < 0) left = 10;

    return { top, left };
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showTaskDueDatePopup || showTaskAssigneePopup || showTaskStatusPopup) {
        const isClickOnCalendar = calendarIconRef.current?.contains(e.target);
        const isClickOnAssignee = assigneeIconRef.current?.contains(e.target);
        const isClickOnStatus = statusIconRef.current?.contains(e.target);

        const isClickInsideCalendar = e.target.closest?.('.calendar-popup-root');
        const isClickInsideAssignee = e.target.closest?.('.user-popup-root');
        const isClickInsideStatus = e.target.closest?.('.status-popup-root');

        if (!isClickOnCalendar && !isClickInsideCalendar) {
          setShowTaskDueDatePopup(false);
          // Reset temp dates when closing
          setTempStartDate(null);
          setTempEndDate(null);
          setCalendarMode('dueDate');
        }
        if (!isClickOnAssignee && !isClickInsideAssignee) {
          setShowTaskAssigneePopup(false);
        }
        if (!isClickOnStatus && !isClickInsideStatus) {
          setShowTaskStatusPopup(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTaskDueDatePopup, showTaskAssigneePopup, showTaskStatusPopup]);

  // Update position on scroll/resize
  useEffect(() => {
    const updatePositions = () => {
      if (showTaskDueDatePopup) {
        setPopupPosition(prev => ({ 
          ...prev, 
          dueDate: calculatePopupPosition(calendarIconRef, 'calendar') 
        }));
      }
      if (showTaskAssigneePopup) {
        setPopupPosition(prev => ({ 
          ...prev, 
          assignee: calculatePopupPosition(assigneeIconRef, 'assignee') 
        }));
      }
      if (showTaskStatusPopup) {
        setPopupPosition(prev => ({ 
          ...prev, 
          status: calculatePopupPosition(statusIconRef, 'status') 
        }));
      }
    };

    if (showTaskDueDatePopup || showTaskAssigneePopup || showTaskStatusPopup) {
      window.addEventListener('scroll', updatePositions, true);
      window.addEventListener('resize', updatePositions);
    }

    return () => {
      window.removeEventListener('scroll', updatePositions, true);
      window.removeEventListener('resize', updatePositions);
    };
  }, [showTaskDueDatePopup, showTaskAssigneePopup, showTaskStatusPopup, calculatePopupPosition]);

  // Calendar functions
  const goToPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateSelect = (date) => {
    if (calendarMode === 'dueDate') {
      // For due date mode, directly update
      handleDueDateSave(date);
    } else {
      // For duration mode, manage temp selections
      if (!tempStartDate) {
        setTempStartDate(date);
        setTempEndDate(null);
      } else if (!tempEndDate && date >= tempStartDate) {
        setTempEndDate(date);
      } else {
        // Reset and start new selection
        setTempStartDate(date);
        setTempEndDate(null);
      }
    }
  };

  const handleDueDateSave = async (date) => {
    setSelectedDueDate(date);
    setShowTaskDueDatePopup(false);
    
    const toastId = toast.loading(date ? 'Updating due date...' : 'Clearing due date...');
    await updateTask({ dueDate: date });
    toast.dismiss(toastId);
  };

  const handleDurationSave = async () => {
    if (tempStartDate && tempEndDate) {
      setSelectedStartDate(tempStartDate);
      setSelectedEndDate(tempEndDate);
      setShowTaskDueDatePopup(false);
      
      const toastId = toast.loading('Updating duration...');
      await updateTask({ 
        startDate: tempStartDate.toISOString(),
        endDate: tempEndDate.toISOString(),
        dueDate: null // Clear due date when setting duration
      });
      toast.dismiss(toastId);
      
      // Reset temp dates
      setTempStartDate(null);
      setTempEndDate(null);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const handleAssigneeSelect = async (user) => {
    // Check if user is already assigned
    const isAlreadyAssigned = selectedAssignees.some(a => a.id === user.id);
    
    if (isAlreadyAssigned) {
      // Remove assignee
      await removeAssignee(user);
    } else {
      // Add assignee
      await addAssignee(user);
    }
    
    setShowTaskAssigneePopup(false);
  };

  const handleStatusSelect = async (status) => {
    setSelectedStatus(status);
    setShowTaskStatusPopup(false);
    
    const toastId = toast.loading(`Changing status to ${status.name}...`);
    await updateTask({ status: status.id });
    toast.dismiss(toastId);
  };

  const toggleCalendarMode = (mode) => {
    setCalendarMode(mode);
    setTempStartDate(null);
    setTempEndDate(null);
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const daysInPrevMonth = new Date(year, month, 0).getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add days from previous month
    for (let i = startingDay - 1; i >= 0; i--) {
      const prevMonthDate = new Date(year, month - 1, daysInPrevMonth - i);
      prevMonthDate.setHours(0, 0, 0, 0);
      
      const isSelected = calendarMode === 'dueDate' 
        ? (selectedDueDate && new Date(selectedDueDate).toDateString() === prevMonthDate.toDateString())
        : (tempStartDate && tempStartDate.toDateString() === prevMonthDate.toDateString()) ||
          (tempEndDate && tempEndDate.toDateString() === prevMonthDate.toDateString());
      
      const isInRange = tempStartDate && tempEndDate && 
        prevMonthDate >= tempStartDate && prevMonthDate <= tempEndDate;
      
      days.push(
        <div 
          key={`prev-${i}`} 
          className={`calendar-day other-month ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''}`}
          onClick={() => handleDateSelect(prevMonthDate)}
          style={{
            backgroundColor: isInRange ? '#e3f2fd' : 'transparent',
          }}
        >
          {daysInPrevMonth - i}
        </div>
      );
    }
    
    // Add days of the current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      
      const isSelected = calendarMode === 'dueDate' 
        ? (selectedDueDate && new Date(selectedDueDate).toDateString() === date.toDateString())
        : (tempStartDate && tempStartDate.toDateString() === date.toDateString()) ||
          (tempEndDate && tempEndDate.toDateString() === date.toDateString());
      
      const isInRange = tempStartDate && tempEndDate && 
        date >= tempStartDate && date <= tempEndDate;
      
      const isToday = date.toDateString() === today.toDateString();
      const isStart = tempStartDate && tempStartDate.toDateString() === date.toDateString();
      const isEnd = tempEndDate && tempEndDate.toDateString() === date.toDateString();
      
      days.push(
        <div 
          key={d} 
          className={`calendar-day current-month ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isStart ? 'range-start' : ''} ${isEnd ? 'range-end' : ''}`}
          onClick={() => handleDateSelect(date)}
          style={{
            backgroundColor: isInRange ? '#e3f2fd' : 'transparent',
            border: isStart || isEnd ? '2px solid #1976d2' : 'none',
          }}
        >
          {d}
        </div>
      );
    }
    
    return days;
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getRandomColor = (id) => {
    const colors = ['#1976d2', '#dc3545', '#28a745', '#fd7e14', '#6f42c1', '#20c997', '#e83e8c', '#007bff'];
    return colors[(id || 0) % colors.length];
  };

  const handleDueDateClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowTaskDueDatePopup(!showTaskDueDatePopup);
    setShowTaskAssigneePopup(false);
    setShowTaskStatusPopup(false);
    setCalendarMode('dueDate');
    setTempStartDate(null);
    setTempEndDate(null);
    setPopupPosition(prev => ({ 
      ...prev, 
      dueDate: calculatePopupPosition(calendarIconRef, 'calendar') 
    }));
  };

  const handleAssigneeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowTaskAssigneePopup(!showTaskAssigneePopup);
    setShowTaskDueDatePopup(false);
    setShowTaskStatusPopup(false);
    setPopupPosition(prev => ({ 
      ...prev, 
      assignee: calculatePopupPosition(assigneeIconRef, 'assignee') 
    }));
  };

  const handleStatusClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowTaskStatusPopup(!showTaskStatusPopup);
    setShowTaskDueDatePopup(false);
    setShowTaskAssigneePopup(false);
    setPopupPosition(prev => ({ 
      ...prev, 
      status: calculatePopupPosition(statusIconRef, 'status') 
    }));
  };

  // Function to render status icon based on status ID
  const renderStatusIcon = () => {
    const statusId = selectedStatus?.id;
    const color = selectedStatus?.color || '#6c757d';
    
    switch(statusId) {
      case 1:
        return <i className="fas fa-check" style={{ fontSize: '14px', color: color }}></i>;
      case 2:
        return <i className="fas fa-check-double" style={{ fontSize: '14px', color: color }}></i>;
      case 3:
        return <i className="fas fa-archive" style={{ fontSize: '16px', color: color }}></i>;
      case 4:
        return <i className="fas fa-times" style={{ fontSize: '16px', color: color }}></i>;
      case 5:
        return <i className="fas fa-check-circle" style={{ fontSize: '16px', color: color }}></i>;
      default:
        return <i className="fas fa-check" style={{ fontSize: '14px', color: '#6c757d' }}></i>;
    }
  };

  // Function to render assignee avatars
  const renderAssigneeAvatars = () => {
    if (selectedAssignees.length === 0) {
      return <span style={{ color: '#999' }}>Unassigned</span>;
    }

    return (
      <div style={{ display: 'flex', fontWeight: '500', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
        {selectedAssignees.map((assignee, index) => (
          <div
            key={assignee.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              backgroundColor: getRandomColor(assignee.id) + '20',
              borderRadius: '12px',
              fontSize: '11px',
            }}
            title={assignee.name}
          >
            <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {assignee.name}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div
        ref={ref}
        data-note-id={note.id}
        data-item-type={note.itemType}
        className={`note-card ${isTask ? 'task-card' : ''} ${selectedRow === note.id ? "selected" : ""}`}
        onClick={() => handleRowClick(note)}
        onDoubleClick={() => handleEdit(note)}
      >
        <NoteHeader
          note={note}
          searchTerm={searchTerm}
          userStatusMap={userStatusMap}
          loadingUsers={loadingUsers}
          handleJobClick={handleJobClick}
          handleTaskClick={handleTaskClick}
          handleUserNameClick={handleUserNameClick}
          isProjectFiltered={isProjectFiltered}
          isJobFiltered={isJobFiltered}
          isTaskFiltered={isTaskFiltered}
          isUserNameFiltered={isUserNameFiltered}
          showJobTooltip={showJobTooltip}
          setShowJobTooltip={setShowJobTooltip}
          showProjectTooltip={showProjectTooltip}
          setShowProjectTooltip={setShowProjectTooltip}
          onFilterByProject={onFilterByProject}
        />

        <NoteContent
          note={note}
          searchTerm={searchTerm}
          viewMode={viewMode}
          onMouseEnter={handleNoteTextMouseEnter}
          onMouseLeave={handleNoteTextMouseLeave}
          shouldShowNotePopup={shouldShowNotePopup}
          
        />

        <NoteFooter
          note={note}
          priorityValue={priorityValue}
          shouldShowLink={shouldShowLink}
          originalNoteId={originalNoteId}
          onViewAttachments={handleViewAttachments}
          onReply={handleReplyToNote}
          onAdd={handleAddFromRow}
          onEdit={handleRowDoubleClick}
          onDelete={handleDelete}
          onPriorityClick={handlePriorityClick}
          onPriorityMouseEnter={handlePriorityMouseEnter}
          onPriorityMouseLeave={handlePriorityMouseLeave}
          onLinkedNoteClick={handleLinkedNoteClick}
          onLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
          onLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
          renderImageIcon={renderCardImageIcon}
          isTask={isTask}
          selectedDueDate={selectedDueDate}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          selectedAssignees={selectedAssignees}
          selectedStatus={selectedStatus}
          onDueDateClick={handleDueDateClick}
          onAssigneeClick={handleAssigneeClick}
          onStatusClick={handleStatusClick}
          calendarIconRef={calendarIconRef}
          assigneeIconRef={assigneeIconRef}
          statusIconRef={statusIconRef}
          updatingTask={updatingTask}
          updatingAssignees={updatingAssignees}
          renderStatusIcon={renderStatusIcon}
          renderAssigneeAvatars={renderAssigneeAvatars}
        />
      </div>

      {/* Render popups at root level using portal */}
      <RootPortal>
        {showTaskDueDatePopup && (
          <CalendarPopup
            position={popupPosition.dueDate}
            currentMonth={currentMonth}
            goToPreviousMonth={goToPreviousMonth}
            goToNextMonth={goToNextMonth}
            generateCalendarDays={generateCalendarDays}
            monthNames={monthNames}
            selectedDueDate={selectedDueDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            tempStartDate={tempStartDate}
            tempEndDate={tempEndDate}
            calendarMode={calendarMode}
            toggleCalendarMode={toggleCalendarMode}
            handleDueDateSave={handleDueDateSave}
            handleDurationSave={handleDurationSave}
            updating={updatingTask}
          />
        )}

        {showTaskAssigneePopup && (
          <AssigneePopup
            position={popupPosition.assignee}
            users={availableUsers}
            loading={loadingUsersList}
            error={usersError}
            selectedAssignees={selectedAssignees}
            onTaskAssigneeSelect={handleAssigneeSelect}
            getInitials={getInitials}
            getRandomColor={getRandomColor}
            updating={updatingAssignees}
          />
        )}

        {showTaskStatusPopup && (
          <StatusPopup
            position={popupPosition.status}
            taskStatuses={taskStatuses}
            selectedStatus={selectedStatus}
            onTaskStatusSelect={handleStatusSelect}
            updating={updatingTask}
          />
        )}
      </RootPortal>
    </>
  );
});

// Calendar Popup Component
const CalendarPopup = ({ 
  position, 
  currentMonth, 
  goToPreviousMonth, 
  goToNextMonth, 
  generateCalendarDays, 
  monthNames, 
  selectedDueDate,
  selectedStartDate,
  selectedEndDate,
  tempStartDate,
  tempEndDate,
  calendarMode,
  toggleCalendarMode,
  handleDueDateSave,
  handleDurationSave,
  updating
}) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const handleSave = () => {
    if (calendarMode === 'dueDate' && selectedDate) {
      handleDueDateSave(selectedDate);
    } else if (calendarMode === 'duration') {
      handleDurationSave();
    }
  };

  return (
    <div
      className="calendar-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "280px",
        padding: "12px",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div style={{ 
          position: "absolute", 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          zIndex: 1
        }}>
          <i className="fas fa-spinner fa-spin" style={{ color: "#1976d2", fontSize: "24px" }}></i>
        </div>
      )}
      
      {/* Mode Toggle */}
      <div style={{
        display: 'flex',
        marginBottom: '12px',
        backgroundColor: '#f5f5f5',
        borderRadius: '6px',
        padding: '2px',
      }}>
        <button
          onClick={() => toggleCalendarMode('dueDate')}
          style={{
            flex: 1,
            padding: '6px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: calendarMode === 'dueDate' ? '#1976d2' : 'transparent',
            color: calendarMode === 'dueDate' ? 'white' : '#666',
            cursor: updating ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          disabled={updating}
        >
          Due Date
        </button>
        <button
          onClick={() => toggleCalendarMode('duration')}
          style={{
            flex: 1,
            padding: '6px',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: calendarMode === 'duration' ? '#1976d2' : 'transparent',
            color: calendarMode === 'duration' ? 'white' : '#666',
            cursor: updating ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            fontWeight: 500,
            transition: 'all 0.2s ease',
          }}
          disabled={updating}
        >
          Duration
        </button>
      </div>

      {/* Selection Info */}
      <div style={{
        marginBottom: '12px',
        padding: '8px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        fontSize: '12px',
      }}>
        {calendarMode === 'dueDate' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-calendar-day" style={{ color: '#1976d2' }}></i>
            <span>
              {selectedDueDate ? 
                `Due Date: ${new Date(selectedDueDate).toLocaleDateString()}` : 
                'No due date set'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-play" style={{ color: '#28a745', fontSize: '10px' }}></i>
              <span>
                Start: {tempStartDate ? tempStartDate.toLocaleDateString() : 
                       (selectedStartDate ? new Date(selectedStartDate).toLocaleDateString() : 'Not selected')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="fas fa-stop" style={{ color: '#dc3545', fontSize: '10px' }}></i>
              <span>
                End: {tempEndDate ? tempEndDate.toLocaleDateString() : 
                     (selectedEndDate ? new Date(selectedEndDate).toLocaleDateString() : 'Not selected')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Calendar Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <button 
          onClick={goToPreviousMonth} 
          disabled={updating}
          style={{ 
            background: "#f0f0f0", 
            border: "1px solid #ddd", 
            borderRadius: "4px", 
            padding: "4px 8px", 
            cursor: updating ? "not-allowed" : "pointer",
            opacity: updating ? 0.5 : 1
          }}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        <span style={{ fontWeight: "bold" }}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button 
          onClick={goToNextMonth} 
          disabled={updating}
          style={{ 
            background: "#f0f0f0", 
            border: "1px solid #ddd", 
            borderRadius: "4px", 
            padding: "4px 8px", 
            cursor: updating ? "not-allowed" : "pointer",
            opacity: updating ? 0.5 : 1
          }}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
      
      {/* Day Labels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", marginBottom: "5px" }}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(day => (
          <div key={day} style={{ textAlign: "center", fontSize: "11px", fontWeight: "bold", color: "#666" }}>{day}</div>
        ))}
      </div>
      
      {/* Calendar Days */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px", marginBottom: "12px" }}>
        {generateCalendarDays()}
      </div>

      {/* Footer with Save Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '1px solid #eee',
        paddingTop: '12px',
        marginTop: '4px',
      }}>
        <button
          onClick={handleSave}
          disabled={updating || (calendarMode === 'duration' && (!tempStartDate || !tempEndDate))}
          style={{
            padding: '6px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            cursor: (updating || (calendarMode === 'duration' && (!tempStartDate || !tempEndDate))) ? 'not-allowed' : 'pointer',
            opacity: (updating || (calendarMode === 'duration' && (!tempStartDate || !tempEndDate))) ? 0.5 : 1,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!updating && !(calendarMode === 'duration' && (!tempStartDate || !tempEndDate))) {
              e.currentTarget.style.backgroundColor = '#1557b0';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#1976d2';
          }}
        >
          Save
        </button>
      </div>

      <style>{`
        .calendar-day {
          text-align: center;
          padding: 6px 2px;
          font-size: 12px;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .calendar-day:hover:not(.disabled) {
          background-color: #e3f2fd !important;
        }
        .calendar-day.selected {
          background-color: #1976d2 !important;
          color: white !important;
        }
        .calendar-day.today {
          border: 1px solid #1976d2;
        }
        .calendar-day.disabled {
          color: #ccc;
          cursor: not-allowed;
        }
        .calendar-day.other-month {
          color: #999;
        }
        .calendar-day.in-range {
          background-color: #e3f2fd;
        }
        .calendar-day.range-start, .calendar-day.range-end {
          background-color: #1976d2 !important;
          color: white !important;
        }
      `}</style>
    </div>
  );
};

// Assignee Popup Component - Updated with minus icon and confirmation
const AssigneePopup = ({ 
  position, 
  users, 
  loading, 
  error,
  selectedAssignees, 
  onTaskAssigneeSelect, 
  getInitials,
  getRandomColor,
  updating
}) => {
  const [userToRemove, setUserToRemove] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleUserClick = (user, isSelected) => {
    if (updating) return;
    
    if (isSelected) {
      // Show confirmation dialog for removal
      setUserToRemove(user);
      setShowConfirmDialog(true);
    } else {
      // Directly add the user
      onTaskAssigneeSelect(user);
    }
  };

  const handleConfirmRemove = () => {
    if (userToRemove) {
      onTaskAssigneeSelect(userToRemove);
      setShowConfirmDialog(false);
      setUserToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
    setUserToRemove(null);
  };

  return (
    <div
      className="user-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "240px",
        maxHeight: "350px",
        display: "flex",
        flexDirection: "column",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div style={{ 
          position: "absolute", 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          zIndex: 2
        }}>
          <i className="fas fa-spinner fa-spin" style={{ color: "#1976d2", fontSize: "24px" }}></i>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 3
        }}>
          <div style={{
            backgroundColor: "white",
            borderRadius: "8px",
            padding: "16px",
            width: "200px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
          }}>
            <div style={{ 
              fontSize: "14px", 
              fontWeight: 600, 
              marginBottom: "12px",
              color: "#333"
            }}>
              Remove Assignee?
            </div>
            <div style={{ 
              fontSize: "13px", 
              marginBottom: "16px",
              color: "#666"
            }}>
              Are you sure you want to remove <strong>{userToRemove?.name}</strong> from this task?
            </div>
            <div style={{
              display: "flex",
              gap: "8px",
              justifyContent: "flex-end"
            }}>
              <button
                onClick={handleCancelRemove}
                style={{
                  padding: "6px 12px",
                  border: "1px solid #ddd",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer",
                  color: "#666"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f5";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRemove}
                style={{
                  padding: "6px 12px",
                  border: "none",
                  backgroundColor: "#dc3545",
                  color: "white",
                  borderRadius: "4px",
                  fontSize: "12px",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#c82333";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc3545";
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ 
        padding: "12px", 
        borderBottom: "1px solid #eee", 
        fontWeight: "bold",
        flexShrink: 0,
        backgroundColor: "#fff",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>Select Assignees</span>
        <span style={{ 
          fontSize: '11px', 
          color: '#666',
          backgroundColor: '#f0f0f0',
          padding: '2px 6px',
          borderRadius: '12px'
        }}>
          {selectedAssignees.length} assigned
        </span>
      </div>
      
      <div style={{ 
        maxHeight: "200px", 
        overflowY: "auto",
        overflowX: "hidden",
        flex: 1
      }}>
        {loading ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
            <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
            Loading users...
          </div>
        ) : error ? (
          <div style={{ padding: "20px", textAlign: "center", color: "#dc3545" }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: "8px" }}></i>
            {error}
          </div>
        ) : users && users.length > 0 ? (
          users.map(user => {
            const isSelected = selectedAssignees.some(a => a.id === user.id);
            
            return (
              <div 
                key={user.id}
                onClick={() => handleUserClick(user, isSelected)}
                style={{
                  padding: "10px 12px",
                  cursor: updating ? "not-allowed" : "pointer",
                  backgroundColor: isSelected ? "#e3f2fd" : "transparent",
                  borderBottom: "1px solid #f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  opacity: updating ? 0.5 : 1,
                  transition: "background-color 0.2s ease",
                  position: "relative"
                }}
                onMouseEnter={(e) => {
                  if (!updating) {
                    e.currentTarget.style.backgroundColor = isSelected ? "#e3f2fd" : "#f5f5f5";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!updating) {
                    e.currentTarget.style.backgroundColor = isSelected ? "#e3f2fd" : "transparent";
                  }
                }}
              >
                <div style={{ 
                  width: "30px", 
                  height: "30px", 
                  borderRadius: "50%", 
                  backgroundColor: user.color || getRandomColor(user.id),
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  fontSize: "12px",
                  flexShrink: 0
                }}>
                  {getInitials(user.name)}
                </div>
                <span style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1
                }}>
                  {user.name}
                </span>
                {isSelected ? (
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <i className="fas fa-check" style={{ color: "#1976d2", flexShrink: 0 }} />
                    <i className="fas fa-minus-circle" style={{ color: "#dc3545", flexShrink: 0, fontSize: '14px' }} />
                  </div>
                ) : (
                  <i className="fas fa-plus-circle" style={{ color: "#28a745", flexShrink: 0 }} title="Click to assign" />
                )}
              </div>
            );
          })
        ) : (
          <div style={{ padding: "20px", textAlign: "center", color: "#999" }}>
            <i className="fas fa-users" style={{ marginRight: "8px", fontSize: "24px", opacity: 0.5 }}></i>
            <div style={{ marginTop: "8px" }}>No users available</div>
          </div>
        )}
      </div>
      
      {/* Selected users summary */}
      {selectedAssignees.length > 0 && (
        <div style={{
          padding: '8px 12px',
          borderTop: '1px solid #eee',
          backgroundColor: '#f8f9fa',
          fontSize: '11px',
          color: '#666',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px'
        }}>
          
        </div>
      )}
    </div>
  );
};

// Status Popup Component
const StatusPopup = ({ 
  position, 
  taskStatuses, 
  selectedStatus, 
  onTaskStatusSelect,
  updating
}) => {
  const defaultStatuses = [
    { id: 1, name: "To Do", icon: "fa-circle", color: "#6c757d", bgColor: "#e9ecef" },
    { id: 2, name: "In Progress", icon: "fa-spinner", color: "#fd7e14", bgColor: "#fff3e0" },
    { id: 3, name: "Archived", icon: "fa-archive", color: "#6c757d", bgColor: "#e9ecef" },
    { id: 4, name: "Blocked", icon: "fa-times", color: "#dc3545", bgColor: "#f8d7da" },
    { id: 5, name: "Done", icon: "fa-check-circle", color: "#28a745", bgColor: "#d4edda" },
  ];

  const statuses = taskStatuses && taskStatuses.length > 0 ? taskStatuses : defaultStatuses;

  return (
    <div
      className="status-popup-root"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 999999,
        backgroundColor: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.2)",
        width: "200px",
        opacity: 1,
        color: "#333333",
        pointerEvents: updating ? "none" : "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {updating && (
        <div style={{ 
          position: "absolute", 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          zIndex: 1
        }}>
          <i className="fas fa-spinner fa-spin" style={{ color: "#1976d2", fontSize: "24px" }}></i>
        </div>
      )}
      <div style={{ padding: "12px", borderBottom: "1px solid #eee", fontWeight: "bold" }}>
        Select Status
      </div>
      
      <div>
        {statuses.map(status => (
          <div 
            key={status.id}
            onClick={() => !updating && onTaskStatusSelect(status)}
            style={{
              padding: "10px 12px",
              cursor: updating ? "not-allowed" : "pointer",
              backgroundColor: selectedStatus?.id === status.id ? "#f8f9fa" : "transparent",
              borderBottom: "1px solid #f5f5f5",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderLeft: selectedStatus?.id === status.id ? `3px solid ${status.color}` : "3px solid transparent",
              opacity: updating ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!updating) {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }
            }}
            onMouseLeave={(e) => {
              if (!updating) {
                e.currentTarget.style.backgroundColor = selectedStatus?.id === status.id ? "#f8f9fa" : "transparent";
              }
            }}
          >
            <div style={{ 
              width: "20px", 
              height: "20px", 
              borderRadius: "4px", 
              backgroundColor: status.bgColor,
              color: status.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              flexShrink: 0
            }}>
              <i className={`fas ${status.icon}`}></i>
            </div>
            <span style={{ flex: 1 }}>{status.name}</span>
            {selectedStatus?.id === status.id && (
              <i className="fas fa-check" style={{ marginLeft: "auto", color: status.color, flexShrink: 0 }}></i>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};


// NoteHeader, NoteContent, NoteFooter components remain the same...
// (Keeping them as in the original for brevity, but they need to be updated to accept the new props)

const NoteHeader = ({ note, searchTerm, userStatusMap, loadingUsers,
  
  handleJobClick,
  handleTaskClick,
  handleUserNameClick,
  isProjectFiltered,
  isJobFiltered,
  isTaskFiltered,
  isUserNameFiltered,
  showJobTooltip,
  setShowJobTooltip,
  showProjectTooltip,
  setShowProjectTooltip,
onFilterByProject }) => (
  <div className="note-header">
    <UserAvatar note={note} 
      handleUserNameClick={handleUserNameClick} 
      isUserNameFiltered={isUserNameFiltered}
      />
    <NoteMeta 
      note={note} 
      userStatusMap={userStatusMap} 
      loadingUsers={loadingUsers} 
      handleUserNameClick={handleUserNameClick} 
      isUserNameFiltered={isUserNameFiltered}
      />
    <NoteContext 
      note={note}
      searchTerm={searchTerm}
      handleJobClick={handleJobClick}
      handleTaskClick={handleTaskClick}
      isProjectFiltered={isProjectFiltered}
      isJobFiltered={isJobFiltered}
      isTaskFiltered={isTaskFiltered}
      showJobTooltip={showJobTooltip}
      setShowJobTooltip={setShowJobTooltip}
      showProjectTooltip={showProjectTooltip}
      setShowProjectTooltip={setShowProjectTooltip}
      onFilterByProject={onFilterByProject}
      />
  </div>
);

const UserAvatar = ({ note, handleUserNameClick, isUserNameFiltered }) => {
  const initials = getNoteInitials(note.userName);
  
  const userNameFiltered = isUserNameFiltered && typeof isUserNameFiltered === 'function' 
    ? isUserNameFiltered(note.userName) 
    : false;
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (handleUserNameClick && note.userName) {
      handleUserNameClick(note.userName);
    }
  };
  
  return (
    
    <div 
      className="user-avatar-wrapper"
      onClick={handleClick}
      style={{
        cursor: handleUserNameClick ? 'pointer' : 'default',
        position: 'relative'
      }}
      title={`Click to filter by user: ${note.userName || "Unknown User"}`}
    >
      <div 
        className="user-avatar"
        style={{
          border: userNameFiltered ? '2px solid #3498db' : 'none',
          boxShadow: userNameFiltered ? '0 0 0 2px rgba(52, 152, 219, 0.3)' : 'none',
          transition: 'all 0.2s ease'
        }}
      >
        {initials ? initials : <i className="fas fa-user" />}
      </div>
      <div className="user-tooltip">
        {note.userName || "Unknown User"}
        {userNameFiltered }
      </div>
    </div>
  );
};

const NoteMeta = ({ 
  note, 
  userStatusMap, 
  loadingUsers,
  handleUserNameClick,
  isUserNameFiltered 
}) => {
  
  const userNameFiltered = isUserNameFiltered && typeof isUserNameFiltered === 'function' 
    ? isUserNameFiltered(note.userName) 
    : false;
  
  const handleUserNameClickHandler = (e) => {
    e.stopPropagation();
    if (handleUserNameClick && note.userId) {
      handleUserNameClick(note.userId);
    }
  };
  
  return (
    <div className="note-meta">
      <div 
        className="note-author" 
        title={note.userName || "Unknown User"}
        onClick={handleUserNameClickHandler}
        style={{
          cursor: handleUserNameClick ? 'pointer' : 'default',
          color: userNameFiltered ? '#3498db' : 'inherit',
          fontWeight: userNameFiltered ? '600' : '700',
          textDecoration: userNameFiltered ? 'underline' : 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 4px',

          borderRadius: '3px',
          backgroundColor: userNameFiltered ? 'rgba(52, 152, 219, 0.1)' : 'transparent',
          transition: 'all 0.2s ease'
        }}
      >
        <UserStatusIndicator
          userId={note.userId}
          userName={note.userName}
          userStatusMap={userStatusMap}
          loadingUsers={loadingUsers}
        />
        {userNameFiltered }
      </div>
      <div className="note-date" title={getFullDate(note.timeStamp)}>
        {formatRelativeTime(note.timeStamp)}
      </div>
    </div>
  );
};

const NoteContext = ({ 
  note, 
  searchTerm,
  handleJobClick,
  handleTaskClick,
  isProjectFiltered,
  isJobFiltered,
  isTaskFiltered,
  onFilterByProject
}) => {
    const handleProjectClick = (e, projectId, projectName) => {
    e.stopPropagation(); // Prevent expanding the stack
    if (projectId && onFilterByProject) {
      onFilterByProject(projectId, projectName);
    }
  };
  const projectFiltered = isProjectFiltered && typeof isProjectFiltered === 'function' 
    ? isProjectFiltered(note.project) 
    : false;
  
  const jobFiltered = isJobFiltered && typeof isJobFiltered === 'function' 
    ? isJobFiltered(note.job) 
    : false;

  const taskFiltered = isTaskFiltered && typeof isTaskFiltered === 'function'
    ? isTaskFiltered(note.title || note.note)
    : false;

  // Check if this is a task
  const isTask = note.itemType === "Task";
  const taskName = note.title || "";

  return (
    <div className="note-context">
       {taskName && (
          <>
            
            <span 
              className="task-name"
              style={{
                color: '#000',
                fontWeight: '700',
                fontSize: "12px",
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                verticalAlign: 'middle'
              }}
              title={taskName}
              onClick={(e) => {
              e.stopPropagation();
              if (handleTaskClick && note.title) {
                handleTaskClick(note.title);
              }
              }}
              
            >
              {taskName.length > 30 ? `${taskName.substring(0, 30)}...` : taskName}
            </span>
          </>
        )}
      <div 
        className="context-item job"
        title={`Click to filter by job: ${note.job}`}
        onClick={(e) => {
          e.stopPropagation();
          if (handleJobClick && note.job) {
            handleJobClick(note.job);
          }
        }}
        style={{
          cursor: 'pointer',
          color: jobFiltered ? '#3498db' : 'inherit',
          fontWeight: jobFiltered ? '600' : 'normal',
          textDecoration: jobFiltered ? 'underline' : 'none'
        }}
      >
        {note.job || "—"}

      </div>
      <div className="context-item workspace-project">
        <span  style={{
            
            maxWidth: '50px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom'
          }}  title={note.workspace}>{note.workspace || "—"}</span>/
        <span 
          title={`Click to filter by project: ${note.project}`}
          onClick={(e) =>handleProjectClick(
                                  e,
                                  note.projectId,
                                  note.projectName,
                                )
                              }
          style={{
            cursor: 'pointer',
            fontWeight: projectFiltered ? '650' : 'normal',
            textDecoration: projectFiltered ? 'underline' : 'none',
            display: 'inline-block',
            maxWidth: '80px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom'
          }}
        >
          {note.project || "—"}
        </span>

      </div>
    </div>
  );
};

const NoteContent = ({ note, searchTerm, viewMode, onMouseEnter, onMouseLeave, shouldShowNotePopup, onFilterByProject }) => {
  const handleMouseEnter = (e) => {
    if (typeof shouldShowNotePopup === 'function' && shouldShowNotePopup(note)) {
      onMouseEnter(note, e);
    }
  };

  const handleMouseLeave = () => {
    if (typeof shouldShowNotePopup === 'function' && shouldShowNotePopup(note)) {
      onMouseLeave();
    }
  };

  // Check if this is a task
  const isTask = note.itemType === "Task";

  // For tasks, use description instead of title/note
  const contentToDisplay = isTask ? (note.description || "") : (note.note || "");

  return (
    <div className="note-content">
      <div
        className="note-card-content-container"
        style={{ position: "relative", height: "100%" }}
        onClick={(e) => {
          if (e.target.tagName === "A" && e.target.classList.contains("note-url-link")) {
            e.stopPropagation();
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="note-text"
          style={{ maxHeight: "80px", overflow: "hidden", position: "relative" }}
          dangerouslySetInnerHTML={{
            __html: highlightHtmlContent(contentToDisplay, searchTerm),
          }}
        />
      </div>
    </div>
  );
};

const NoteFooter = ({
  note,
  priorityValue,
  shouldShowLink,
  originalNoteId,
  onViewAttachments,
  onReply,
  onAdd,
  onEdit,
  onDelete,
  onPriorityClick,
  onPriorityMouseEnter,
  onPriorityMouseLeave,
  onLinkedNoteClick,
  onLinkedNoteMouseEnter,
  onLinkedNoteMouseLeave,
  renderImageIcon,
  isTask,
  selectedDueDate,
  selectedStartDate,
  selectedEndDate,
  selectedAssignees,
  selectedStatus,
  onDueDateClick,
  onAssigneeClick,
  onStatusClick,
  calendarIconRef,
  assigneeIconRef,
  statusIconRef,
  updatingTask,
  updatingAssignees,
  renderStatusIcon,
  renderAssigneeAvatars,
}) => {
  // Format dates for display
  const dueDate = selectedDueDate ? new Date(selectedDueDate) : null;
  const startDate = selectedStartDate ? new Date(selectedStartDate) : null;
  const endDate = selectedEndDate ? new Date(selectedEndDate) : null;
  const isOverdue = dueDate && dueDate < new Date() && selectedStatus?.id !== 4;

  return (
    <div className="note-footer">
      <div className="note-attachments">
        {/* For tasks: only show task-related icons */}
        {isTask ? (
          <>
            <div ref={calendarIconRef} style={{ display: 'inline-block', position: 'relative' }}>
              <div
                className={`task-icon-item ${selectedDueDate || (selectedStartDate && selectedEndDate) ? 'has-value' : ''} ${updatingTask ? 'updating' : ''}`}
                title={selectedDueDate ? `Due: ${dueDate.toLocaleDateString()}` : 
                       (selectedStartDate && selectedEndDate) ? `Duration: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : 
                       "Set Due Date or Duration"}
                onClick={onDueDateClick}
                style={{ 
                  opacity: updatingTask ? 0.5 : 1, 
                  cursor: updatingTask ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  backgroundColor: (selectedDueDate || (selectedStartDate && selectedEndDate)) ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.2s ease'
                }}
              >
                <i className="fas fa-calendar" style={{ color: (selectedDueDate || (selectedStartDate && selectedEndDate)) ? '#1976d2' : '#666' }}></i>
              </div>
            </div>

            <div ref={assigneeIconRef} style={{ display: 'inline-block', position: 'relative' }}>
              <div
                className={`task-icon-item ${selectedAssignees.length > 0 ? 'has-value' : ''} ${updatingAssignees ? 'updating' : ''}`}
                title={`Assignees: ${selectedAssignees.length > 0 ? selectedAssignees.map(a => a.name).join(', ') : 'Unassigned'}`}
                onClick={onAssigneeClick}
                style={{ 
                  opacity: updatingAssignees ? 0.5 : 1, 
                  cursor: updatingAssignees ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  backgroundColor: selectedAssignees.length > 0 ? '#e3f2fd' : 'transparent',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                <i className="fas fa-user" style={{ color: selectedAssignees.length > 0 ? '#1976d2' : '#666' }}></i>
                {selectedAssignees.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    backgroundColor: '#fd7e14',
                    color: 'white',
                    borderRadius: '50%',
                    width: '14px',
                    height: '14px',
                    fontSize: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {selectedAssignees.length}
                  </span>
                )}
              </div>
            </div>

            <div ref={statusIconRef} style={{ display: 'inline-block', position: 'relative' }}>
              <div
                className={`task-icon-item ${selectedStatus ? 'has-value' : ''} ${updatingTask ? 'updating' : ''}`}
                title={`Status: ${selectedStatus?.name || 'To Do'}`}
                onClick={onStatusClick}
                style={{
                  opacity: updatingTask ? 0.5 : 1,
                  cursor: updatingTask ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  minWidth: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  backgroundColor: selectedStatus ? `${selectedStatus.bgColor}` : 'transparent',
                  transition: 'all 0.2s ease'
                }}
              >
                {renderStatusIcon()}
              </div>
            </div>
          </>
        ) : (
          /* For regular notes: show the original icons */
          <>
            {renderImageIcon && renderImageIcon(note)}
            <AttachmentButton note={note} onClick={onViewAttachments} />
            <ReplyButton note={note} onClick={onReply} />
            {shouldShowLink && originalNoteId && (
              <LinkButton
                noteId={note.id}
                onClick={onLinkedNoteClick}
                onMouseEnter={onLinkedNoteMouseEnter}
                onMouseLeave={onLinkedNoteMouseLeave}
              />
            )}
          </>
        )}
      </div>

      <div className="note-actions">
        {/* Priority indicator - only show for non-tasks */}
        {!isTask && (
          <PriorityIndicator
            priorityValue={priorityValue}
            note={note}
            onClick={onPriorityClick}
            onMouseEnter={onPriorityMouseEnter}
            onMouseLeave={onPriorityMouseLeave}
          />
        )}
        
        {/* Add button - common for both */}
        <ActionButton icon="fas fa-plus" title="Add New Note" onClick={() => {onAdd(note)}} />
        
        {/* Edit button - only show for normal cards*/}
        {!isTask && (
          <ActionButton icon="fas fa-external-link-alt" title="View Note" onClick={() => onEdit(note)} />
        )}
        
        {/* Delete button - only show for non-tasks */}
        {!isTask && (
          <ActionButton icon="fas fa-trash" title="Delete Note" onClick={() => onDelete(note)} />
        )}
      </div>
    </div>
  );
};

const AttachmentButton = ({ note, onClick }) => (
  <button 
    className="attachment-btn" 
    onClick={(e) => { 
      e.stopPropagation(); 
      onClick(note); 
    }}
  >
    <i className="fas fa-paperclip" style={{ opacity: note.documentCount > 0 ? 1 : 0.3 }} />
    <span>({note.documentCount || 0})</span>
  </button>
);

const ReplyButton = ({note, onClick }) => (
  <button
    className="attachment-btn"
    onClick={(e) => { 
      e.stopPropagation(); 
      onClick(note); 
    }}
    title="Reply to this note"
  >
    <i className="fas fa-reply" />
  </button>
);

const LinkButton = ({ noteId, onClick, onMouseEnter, onMouseLeave }) => (
  <button
    className="link-to-note-btn"
    onClick={(e) => onClick(noteId, e)}
    onMouseEnter={(e) => onMouseEnter(noteId, e)}
    onMouseLeave={onMouseLeave}
  >
    <i className="fas fa-link" />
  </button>
);

const ActionButton = ({ icon, title, onClick }) => (
  <button 
    className="action-btn" 
    onClick={(e) => { 
      e.stopPropagation(); 
      onClick(); 
    }} 
    title={title}
  >
    <i className={icon} />
  </button>
);

NoteCard.propTypes = {
  note: PropTypes.object.isRequired,
  displayNotes: PropTypes.array, 
  isOriginalNoteExists: PropTypes.func,
  selectedRow: PropTypes.any,
  searchTerm: PropTypes.string,
  viewMode: PropTypes.string,
  handleRowClick: PropTypes.func.isRequired,
  handleRowDoubleClick: PropTypes.func.isRequired,
  handleAddFromRow: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleViewAttachments: PropTypes.func.isRequired,
  handleReplyToNote: PropTypes.func.isRequired,
  handlePriorityClick: PropTypes.func.isRequired,
  handlePriorityMouseEnter: PropTypes.func.isRequired,
  handlePriorityMouseLeave: PropTypes.func.isRequired,
  handleNoteTextMouseEnter: PropTypes.func.isRequired,
  handleNoteTextMouseLeave: PropTypes.func.isRequired,
  handleLinkedNoteClick: PropTypes.func.isRequired,
  handleLinkedNoteMouseEnter: PropTypes.func.isRequired,
  handleLinkedNoteMouseLeave: PropTypes.func.isRequired,
  renderCardImageIcon: PropTypes.func,
  isNoteReply: PropTypes.func.isRequired,
  getReplyNoteId: PropTypes.func.isRequired,
  userStatusMap: PropTypes.object.isRequired,
  loadingUsers: PropTypes.object.isRequired,
  getPriorityValue: PropTypes.func.isRequired,
  manuallyUpdatedPriorities: PropTypes.object.isRequired,
  shouldShowNotePopup: PropTypes.func.isRequired,
  handleProjectClick: PropTypes.func,
  handleJobClick: PropTypes.func,
  handleTaskClick: PropTypes.func,
  handleUserNameClick: PropTypes.func,
  isUserNameFiltered: PropTypes.func,
  isProjectFiltered: PropTypes.func,
  isJobFiltered: PropTypes.func,
  isTaskFiltered: PropTypes.func,
  // Task-specific props
  taskData: PropTypes.object,
  onTaskDueDateSelect: PropTypes.func,
  onTaskAssigneeSelect: PropTypes.func,
  onTaskStatusSelect: PropTypes.func,
  taskUsers: PropTypes.array,
  taskStatuses: PropTypes.array,
  loadingTaskUsers: PropTypes.bool,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshNotes: PropTypes.func,
  job: PropTypes.object,
};

NoteCard.defaultProps = {
  displayNotes: [],
  isOriginalNoteExists: () => false,
  selectedRow: null,
  searchTerm: "",
  viewMode: "cards",
  renderCardImageIcon: () => null,
  handleProjectClick: () => {},
  handleJobClick: () => {},
  handleTaskClick: () => {},
  handleUserNameClick: () => {},
  isProjectFiltered: () => false, 
  isJobFiltered: () => false,
  isTaskFiltered: () => false,
  isUserNameFiltered: () => false,
  // Task-specific default props
  taskData: {},
  onTaskDueDateSelect: () => {},
  onTaskAssigneeSelect: () => {},
  onTaskStatusSelect: () => {},
  taskUsers: [],
  taskStatuses: [],
  loadingTaskUsers: false,
  refreshNotes: () => {},
  job: null,
};

export default NoteCard;
